/**
 * Storage Utilities
 * 
 * Provides secure wrappers for browser storage mechanisms including
 * cookies, localStorage, and sessionStorage.
 */

/**
 * Cookie options for setting cookies
 */
export interface CookieOptions {
  expires?: Date | number; // Date object or days from now
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
  httpOnly?: boolean; // Note: Cannot be set via JavaScript, only server-side
}

/**
 * Set a cookie with security options
 * 
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options (secure, sameSite, etc.)
 */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  if (options.expires) {
    let expiresDate: Date;
    
    if (typeof options.expires === 'number') {
      // Treat as days from now
      expiresDate = new Date();
      expiresDate.setTime(expiresDate.getTime() + options.expires * 24 * 60 * 60 * 1000);
    } else {
      expiresDate = options.expires;
    }
    
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }
  
  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += '; path=/';
  }
  
  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }
  
  // Always enforce secure cookies (HTTPS-only)
  // Only allow insecure cookies in development on localhost
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDevelopment = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') || import.meta.env?.DEV;
  
  if (options.secure !== false || (!isLocalhost && !isDevelopment)) {
    cookieString += '; secure';
  }
  
  if (options.sameSite) {
    cookieString += `; samesite=${options.sameSite}`;
  } else {
    // Default to Strict for best security (Requirements 11.1, 11.2)
    cookieString += '; samesite=Strict';
  }
  
  // Note: HttpOnly cannot be set via JavaScript
  // It must be set by the server
  
  document.cookie = cookieString;
}

/**
 * Get a cookie value by name
 * 
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');
  
  for (let cookie of cookies) {
    cookie = cookie.trim();
    
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  
  return null;
}

/**
 * Delete a cookie by name
 * 
 * @param name - Cookie name
 * @param options - Cookie options (path, domain) must match the original cookie
 */
export function deleteCookie(name: string, options: Pick<CookieOptions, 'path' | 'domain'> = {}): void {
  setCookie(name, '', {
    ...options,
    expires: new Date(0), // Set to past date
  });
}

/**
 * Storage type
 */
export type StorageType = 'local' | 'session';

/**
 * Get the appropriate storage object
 * 
 * @param storage - Storage type ('local' or 'session')
 * @returns Storage object
 */
function getStorage(storage: StorageType): Storage {
  return storage === 'local' ? localStorage : sessionStorage;
}

/**
 * Set an item in storage
 * 
 * Automatically serializes objects to JSON.
 * 
 * @param key - Storage key
 * @param value - Value to store (will be JSON stringified)
 * @param storage - Storage type ('local' or 'session')
 */
export function setItem(key: string, value: any, storage: StorageType = 'local'): void {
  try {
    const storageObj = getStorage(storage);
    const serialized = JSON.stringify(value);
    storageObj.setItem(key, serialized);
  } catch (error) {
    console.error(`Failed to set item in ${storage}Storage:`, error);
  }
}

/**
 * Get an item from storage
 * 
 * Automatically deserializes JSON objects.
 * 
 * @param key - Storage key
 * @param storage - Storage type ('local' or 'session')
 * @returns Parsed value or null if not found
 */
export function getItem<T = any>(key: string, storage: StorageType = 'local'): T | null {
  try {
    const storageObj = getStorage(storage);
    const item = storageObj.getItem(key);
    
    if (item === null) {
      return null;
    }
    
    return JSON.parse(item) as T;
  } catch (error) {
    console.error(`Failed to get item from ${storage}Storage:`, error);
    return null;
  }
}

/**
 * Remove an item from storage
 * 
 * @param key - Storage key
 * @param storage - Storage type ('local' or 'session')
 */
export function removeItem(key: string, storage: StorageType = 'local'): void {
  try {
    const storageObj = getStorage(storage);
    storageObj.removeItem(key);
  } catch (error) {
    console.error(`Failed to remove item from ${storage}Storage:`, error);
  }
}

/**
 * Clear all items from storage
 * 
 * @param storage - Storage type ('local' or 'session')
 */
export function clearStorage(storage: StorageType = 'local'): void {
  try {
    const storageObj = getStorage(storage);
    storageObj.clear();
  } catch (error) {
    console.error(`Failed to clear ${storage}Storage:`, error);
  }
}

/**
 * Check if storage is available
 * 
 * @param storage - Storage type ('local' or 'session')
 * @returns True if storage is available
 */
export function isStorageAvailable(storage: StorageType = 'local'): boolean {
  try {
    const storageObj = getStorage(storage);
    const testKey = '__storage_test__';
    storageObj.setItem(testKey, 'test');
    storageObj.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}
